import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AcademicCapIcon,
  PlayCircleIcon,
  HomeIcon,
  UserIcon,
  MegaphoneIcon,
  TrophyIcon,
  UserGroupIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';

const getStudyRoomName = (): string => {
  const hostname = window.location.hostname;
  if (hostname === 'eruspace.newattend.com') return '이루는 자리';
  return 'NewAttend';
};

const isCloudDomain = (): boolean => {
  const hostname = window.location.hostname;
  return hostname === 'studyroom.newattend.com' || hostname === 'studyroom.faceattend.store' || hostname.startsWith('studyroom.');
};

const getApiBaseUrl = (): string => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost') return 'http://localhost:8000';
  return '';
};

interface Notice { id: string; title: string; content: string; is_pinned: boolean; created_at: string | null; }
interface RankingEntry { rank: number; name: string; total_minutes: number; study_hours: string; session_count: number; }
interface DisplayData {
  notices: Notice[];
  weekly_rankings: RankingEntry[];
  monthly_rankings: RankingEntry[];
  org_name: string;
  current_students: number;
  as_of: string;
}

const StudyRoomHomePage: React.FC = () => {
  const navigate = useNavigate();
  const studyRoomName = getStudyRoomName();
  const showCloudFeatures = isCloudDomain();

  const [displayData, setDisplayData] = useState<DisplayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleLoginClick = () => {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '192.168.0.38' || h === '180.70.238.90') navigate('/studyroom/owner/login');
    else navigate('/owner/login');
  };
  const handleOwnerSignupClick = () => {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '192.168.0.38' || h === '180.70.238.90') navigate('/studyroom/owner/signup');
    else navigate('/owner/signup');
  };
  const handleParentLoginClick = () => {
    // 학부모 화면은 Flutter Web 이고, 웹은 자기가 올라간 주소를 서버로 쓴다.
    // 그래서 매장 자체 서버를 쓰는 곳은 본부 주소로 보내면 계정이 없어 로그인 자체가 안 된다.
    // → 본부 도메인일 때만 본부 학부모 주소, 나머지는 자기 주소 안의 /parent/ 로 보낸다.
    const h = window.location.hostname;
    const HQ_HOSTS = ['app.newattend.com', 'studyroom.newattend.com', 'newattend.com', 'www.newattend.com'];
    if (h === 'localhost' || h === '192.168.0.38' || h === '180.70.238.90') {
      navigate('/studyroom/parent/login');
    } else if (h.startsWith('stg.') || h.startsWith('stg-')) {
      window.location.href = 'https://stg-parent.newattend.com/';
    } else if (HQ_HOSTS.includes(h)) {
      window.location.href = 'https://parent.newattend.com/';
    } else {
      window.location.href = '/parent/';
    }
  };
  const handleMainHomeClick = () => {
    const h = window.location.hostname;
    if (h === '192.168.0.38' || h === '180.70.238.90') navigate('/');
    else window.location.href = 'https://newattend.com';
  };

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code) navigate(`/auth/kakao/callback${window.location.search}${window.location.hash}`);
  }, [navigate]);

  const fetchDisplayData = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      // 마케팅용 통합 집계: 본부/STG/스튜디오룸 도메인은 모든 가맹점 학생 통합
      // 개별 매장 도메인(eruspace, daegu-school 등)만 매장 데이터
      const hostname = window.location.hostname;
      const franchiseHostnames = [
        'eruspace.newattend.com',
        'daegu-school.newattend.com',
        // 향후 신규 가맹점 추가
      ];
      // 매장 도메인이 아닌 경우 모두 통합 집계
      const useAggregate = !franchiseHostnames.includes(hostname);
      const params = useAggregate ? '?aggregate=true' : '';
      const response = await axios.get(`${baseUrl}/api/v1/public/display/summary${params}`);
      setDisplayData(response.data);
    } catch (error) {
      console.error('전광판 데이터 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDisplayData();
    const interval = setInterval(fetchDisplayData, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchDisplayData]);

  const orgName = displayData?.org_name || studyRoomName;

  const medalBg: Record<number, string> = { 1: 'bg-yellow-400 text-white', 2: 'bg-gray-300 text-white', 3: 'bg-orange-300 text-white' };
  const timeColor: Record<number, string> = { 1: 'text-yellow-600', 2: 'text-gray-500', 3: 'text-orange-500' };

  const RankingList = ({ entries, max = 5 }: { entries: RankingEntry[]; max?: number }) => (
    <div className="space-y-0.5">
      {entries.slice(0, max).map((entry) => {
        const isTop3 = entry.rank <= 3;
        return (
          <div key={entry.rank} className="flex items-center gap-3 py-2">
            {isTop3 ? (
              <div className={`w-8 h-8 rounded-full ${medalBg[entry.rank]} flex items-center justify-center font-bold text-sm shrink-0`}>{entry.rank}</div>
            ) : (
              <div className="w-8 h-8 flex items-center justify-center text-base font-semibold text-gray-300 shrink-0">{entry.rank}</div>
            )}
            <span className={`flex-1 font-semibold ${isTop3 ? 'text-base text-gray-900' : 'text-sm text-gray-600'}`}>{entry.name}</span>
            <span className={`font-bold tabular-nums ${isTop3 ? 'text-base' : 'text-sm'} ${timeColor[entry.rank] || 'text-gray-500'}`}>{entry.study_hours}</span>
          </div>
        );
      })}
      {entries.length === 0 && (
        <div className="text-gray-300 text-sm py-8 text-center">데이터가 없습니다</div>
      )}
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-white overflow-hidden">
      {/* 헤더 */}
      <header className="shrink-0 px-8 py-3 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-green-600 rounded-full flex items-center justify-center">
            <AcademicCapIcon className="h-5 w-5 text-white" />
          </div>
          <span className="text-xl font-bold text-gray-900">{orgName}</span>
        </div>
        <div className="flex items-center gap-5">
          <span className="text-sm font-mono text-gray-400 hidden md:block">
            {currentTime.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' })}
            {' · '}
            {currentTime.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
          </span>
          {showCloudFeatures && (
            <>
              <button onClick={handleMainHomeClick} className="text-sm text-gray-400 hover:text-gray-600">홈페이지</button>
              <button onClick={() => navigate('/landing')} className="text-sm text-blue-500">데모</button>
              <button onClick={handleOwnerSignupClick} className="text-sm text-white bg-blue-600 px-4 py-1.5 rounded-lg">오너 가입</button>
            </>
          )}
          <button onClick={handleLoginClick} className="text-sm text-white bg-green-600 px-4 py-1.5 rounded-lg">관리자</button>
        </div>
      </header>

      {/* 메인 */}
      <main className="flex-1 flex flex-col min-h-0 py-5 px-8 max-w-6xl mx-auto w-full">
        {/* 상단 요약 */}
        <div className="shrink-0 flex items-center justify-center gap-12 py-3 mb-5">
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold text-green-600">{displayData?.current_students ?? 0}</span>
            <span className="text-lg text-gray-400">명 재실</span>
          </div>
          <div className="w-px h-8 bg-gray-200" />
          <button onClick={handleParentLoginClick} className="flex items-baseline gap-1 group">
            <span className="text-xl font-semibold text-purple-600 group-hover:text-purple-700">학부모 로그인</span>
            <ChevronRightIcon className="h-4 w-4 text-purple-400 self-center" />
          </button>
        </div>

        {/* 3컬럼: 공지 | 이번 주 | 이번 달 */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-8 min-h-0">
          {/* 공지사항 */}
          <div className="flex flex-col min-h-0">
            <div className="flex items-center gap-2 mb-4 shrink-0">
              <MegaphoneIcon className="h-5 w-5 text-red-400" />
              <h2 className="text-lg font-bold text-gray-900">공지사항</h2>
            </div>
            <div className="flex-1 overflow-auto min-h-0">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-7 w-7 border-2 border-green-500 border-t-transparent" />
                </div>
              ) : displayData?.notices && displayData.notices.length > 0 ? (
                <div className="space-y-4">
                  {displayData.notices.map((notice) => (
                    <div key={notice.id}>
                      <div className="flex items-center gap-2 mb-1">
                        {notice.is_pinned && (
                          <span className="text-[11px] bg-red-500 text-white px-2 py-0.5 rounded font-medium">중요</span>
                        )}
                        <h4 className="font-bold text-gray-900 text-base">{notice.title}</h4>
                      </div>
                      <p className="text-gray-400 text-sm leading-relaxed">{notice.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-300 text-base">
                  공지사항이 없습니다
                </div>
              )}
            </div>
          </div>

          {/* 이번 주 랭킹 */}
          <div className="flex flex-col min-h-0">
            <div className="flex items-center gap-2 mb-4 shrink-0">
              <TrophyIcon className="h-5 w-5 text-yellow-500" />
              <h2 className="text-lg font-bold text-gray-900">이번 주</h2>
            </div>
            <div className="flex-1 overflow-auto min-h-0">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-7 w-7 border-2 border-green-500 border-t-transparent" />
                </div>
              ) : (
                <RankingList entries={displayData?.weekly_rankings || []} />
              )}
            </div>
          </div>

          {/* 이번 달 랭킹 */}
          <div className="flex flex-col min-h-0">
            <div className="flex items-center gap-2 mb-4 shrink-0">
              <TrophyIcon className="h-5 w-5 text-green-500" />
              <h2 className="text-lg font-bold text-gray-900">이번 달</h2>
            </div>
            <div className="flex-1 overflow-auto min-h-0">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-7 w-7 border-2 border-green-500 border-t-transparent" />
                </div>
              ) : (
                <RankingList entries={displayData?.monthly_rankings || []} />
              )}
            </div>
          </div>
        </div>

        <div className="shrink-0 text-center text-gray-300 text-xs pt-2">
          1시간마다 자동 갱신 · © NTD NewAttend
        </div>
      </main>
    </div>
  );
};

export default StudyRoomHomePage;
